import { ApiError, type DocumentContent, type RevisionMeta } from '@estudio/contracts';
import type { DraftStore, PendingDraft } from '@estudio/desktop-contract';
import { AutosaveScheduler, canonicalJson, type AutosaveOptions, type SaveState } from '@estudio/domain';
import { newOperationId, type CloudClient } from './client.ts';
import { NetworkError } from './transport.ts';

export interface HeadRevision {
  id: string | null;
  number: number | null;
  content: DocumentContent | null;
  createdAt: string | null;
}

export interface DocumentSyncSnapshot {
  documentId: string;
  state: SaveState;
  head: HeadRevision;
  localContent: DocumentContent | null;
  dirty: boolean;
  deletedRemotely: boolean;
  openConflictIds: string[];
}

export interface DocumentSyncDeps {
  client: CloudClient;
  drafts: DraftStore;
  workspaceId: string;
  deviceId?: string | null;
  autosave?: AutosaveOptions;
  /** Reintento en segundo plano cuando no hay conexión (ms). */
  retryMs?: number;
  now?: () => string;
}

/**
 * Sincroniza un documento: autoguardado con revisiones confirmadas por el
 * servidor, pendientes locales y conflictos sin pérdida.
 *
 * Reglas:
 * - Cada contenido pendiente tiene su operation_id; reintentar reenvía el mismo
 *   id y cuerpo (el servidor no duplica la revisión).
 * - Si el resultado de un envío es desconocido (timeout), se reenvía ese mismo
 *   envío antes de mandar contenido más nuevo, para conocer la base real.
 * - El pendiente solo se retira después de la confirmación del servidor.
 * - REVISION_CONFLICT: la propuesta quedó guardada como rama; se deja de
 *   autoguardar hasta que la persona resuelva.
 * - DOCUMENT_DELETED: no se resucita; se ofrece recuperar como documento nuevo.
 */
export class DocumentSync {
  private snapshot: DocumentSyncSnapshot;
  private readonly listeners = new Set<(s: DocumentSyncSnapshot) => void>();
  private readonly scheduler: AutosaveScheduler;
  private draft: PendingDraft | null = null;
  private unconfirmed: PendingDraft | null = null;
  private inflight: Promise<void> | null = null;
  private rerun = false;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private disposed = false;
  private refreshing = false;

  constructor(private readonly deps: DocumentSyncDeps, readonly documentId: string) {
    this.snapshot = {
      documentId,
      state: { kind: 'idle' },
      head: { id: null, number: null, content: null, createdAt: null },
      localContent: null,
      dirty: false,
      deletedRemotely: false,
      openConflictIds: [],
    };
    this.scheduler = new AutosaveScheduler(() => void this.saveNow(), deps.autosave);
  }

  get current(): DocumentSyncSnapshot {
    return this.snapshot;
  }

  subscribe(listener: (s: DocumentSyncSnapshot) => void): () => void {
    this.listeners.add(listener);
    listener(this.snapshot);
    return () => this.listeners.delete(listener);
  }

  private set(patch: Partial<DocumentSyncSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...patch };
    this.listeners.forEach((l) => l(this.snapshot));
  }

  /** Carga la revisión confirmada y recupera pendientes locales si existen. */
  async load(): Promise<DocumentSyncSnapshot> {
    const res = await this.deps.client.call('document.get', { document_id: this.documentId });
    const head = res.head_revision;
    const deleted = res.document.deleted_at !== null;
    this.set({
      head: {
        id: head?.id ?? null,
        number: head?.number ?? null,
        content: (head?.content as DocumentContent | undefined) ?? null,
        createdAt: head?.created_at ?? null,
      },
      localContent: (head?.content as DocumentContent | undefined) ?? null,
      deletedRemotely: deleted,
      openConflictIds: res.open_conflicts.map((c) => c.id),
      state: res.open_conflicts.length ? { kind: 'conflict', conflictId: res.open_conflicts[0]!.id } : head ? { kind: 'saved', serverTime: head.created_at, revisionNumber: head.number } : { kind: 'idle' },
    });
    const pending = await this.deps.drafts.get(this.documentId);
    if (pending && pending.state === 'pending') {
      this.draft = pending;
      this.unconfirmed = pending;
      this.set({ localContent: pending.content as DocumentContent, dirty: true, state: { kind: 'pending_offline', since: pending.updatedAt } });
      if (deleted) await this.markDeleted();
      else void this.saveNow();
    } else if (pending && pending.state !== 'pending') {
      this.draft = pending;
      this.set({ localContent: pending.content as DocumentContent, dirty: true });
    }
    return this.snapshot;
  }

  /** Registra una edición local: se guarda en el almacén de pendientes y se programa el autoguardado. */
  async edit(content: DocumentContent): Promise<void> {
    if (this.snapshot.deletedRemotely) return;
    if (this.draft && canonicalJson(this.draft.content) === canonicalJson(content)) return;
    if (!this.draft && this.snapshot.head.content && canonicalJson(this.snapshot.head.content) === canonicalJson(content)) return;
    this.draft = {
      documentId: this.documentId,
      workspaceId: this.deps.workspaceId,
      baseRevisionId: this.snapshot.head.id,
      operationId: newOperationId(),
      content,
      updatedAt: this.deps.now?.() ?? new Date().toISOString(),
      state: this.snapshot.state.kind === 'conflict' ? 'conflict' : 'pending',
    };
    await this.deps.drafts.put(this.draft);
    this.set({ localContent: content, dirty: true });
    if (this.snapshot.state.kind !== 'conflict') this.scheduler.notifyChange();
  }

  /** Guarda de inmediato (antes de salir, aprobar o cambiar de documento). */
  async flush(): Promise<void> {
    this.scheduler.cancel();
    await this.saveNow();
  }

  /** Reintenta pendientes (al reconectar o recuperar foco). */
  retry(): void {
    if (this.unconfirmed || (this.draft && this.draft.state === 'pending')) void this.saveNow();
  }

  /** Refresh a clean editor after another device saved, without replacing local edits. */
  async refreshRemote(): Promise<void> {
    if(this.disposed||this.snapshot.dirty||this.inflight||this.refreshing)return;
    this.refreshing=true;
    try {
    const res=await this.deps.client.call('document.get',{document_id:this.documentId});
    if(this.disposed||this.snapshot.dirty||this.inflight)return;
    const head=res.head_revision,deleted=res.document.deleted_at!==null;
    if(head?.id===this.snapshot.head.id&&deleted===this.snapshot.deletedRemotely)return;
    const content=(head?.content as DocumentContent|undefined)??null;
    this.set({head:{id:head?.id??null,number:head?.number??null,content,createdAt:head?.created_at??null},localContent:content,deletedRemotely:deleted,openConflictIds:res.open_conflicts.map(c=>c.id),state:res.open_conflicts.length?{kind:'conflict',conflictId:res.open_conflicts[0]!.id}:head?{kind:'saved',serverTime:head.created_at,revisionNumber:head.number}:{kind:'idle'}});
    } finally {this.refreshing=false;}
  }

  dispose(): void {
    this.disposed = true;
    this.scheduler.cancel();
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.listeners.clear();
  }

  private async saveNow(): Promise<void> {
    if (this.inflight) {
      this.rerun = true;
      return this.inflight;
    }
    this.inflight = this.runSave().finally(() => {
      this.inflight = null;
      if (this.rerun && !this.disposed) {
        this.rerun = false;
        void this.saveNow();
      }
    });
    return this.inflight;
  }

  private async runSave(): Promise<void> {
    // Primero se aclara un envío previo de resultado desconocido.
    const toSend = this.unconfirmed ?? (this.draft && this.draft.state === 'pending' ? this.draft : null);
    if (!toSend || this.snapshot.deletedRemotely) return;
    this.set({ state: { kind: 'saving' } });
    this.unconfirmed = toSend;
    try {
      const res = await this.deps.client.call('document.save', {
        document_id: this.documentId,
        operation_id: toSend.operationId,
        base_revision_id: toSend.baseRevisionId,
        content: toSend.content as DocumentContent,
        ...(this.deps.deviceId ? { device_id: this.deps.deviceId } : {}),
      });
      this.unconfirmed = null;
      const rev = res.revision as RevisionMeta | undefined;
      if (rev) {
        this.set({ head: { id: rev.id, number: rev.number, content: toSend.content as DocumentContent, createdAt: rev.created_at } });
      }
      await this.deps.drafts.remove(this.documentId, toSend.operationId);
      if (this.draft && this.draft.operationId !== toSend.operationId) {
        // Hubo ediciones durante el envío: rebasan sobre la revisión confirmada.
        this.draft = { ...this.draft, baseRevisionId: this.snapshot.head.id, operationId: newOperationId() };
        await this.deps.drafts.put(this.draft);
        this.rerun = true;
        this.set({ state: { kind: 'saved', serverTime: rev?.created_at ?? new Date().toISOString(), revisionNumber: rev?.number }, dirty: true });
      } else {
        this.draft = null;
        this.set({ state: { kind: 'saved', serverTime: rev?.created_at ?? new Date().toISOString(), revisionNumber: rev?.number }, dirty: false });
      }
    } catch (e) {
      await this.handleError(e, toSend);
    }
  }

  private async handleError(e: unknown, sent: PendingDraft): Promise<void> {
    if (e instanceof NetworkError) {
      this.set({ state: { kind: 'pending_offline', since: sent.updatedAt } });
      this.scheduleRetry();
      return;
    }
    if (e instanceof ApiError) {
      if (e.code === 'REVISION_CONFLICT') {
        this.unconfirmed = null;
        const conflictId = (e.details?.conflict_id as string | undefined) ?? null;
        // La propuesta ya está guardada en el servidor como rama recuperable.
        await this.deps.drafts.remove(this.documentId, sent.operationId);
        if (this.draft && this.draft.operationId !== sent.operationId) {
          this.draft = { ...this.draft, state: 'conflict', conflictId };
          await this.deps.drafts.put(this.draft);
        } else {
          this.draft = null;
        }
        this.set({ state: { kind: 'conflict', conflictId }, openConflictIds: conflictId ? [...new Set([...this.snapshot.openConflictIds, conflictId])] : this.snapshot.openConflictIds, dirty: this.draft !== null });
        await this.refreshHead();
        return;
      }
      if (e.code === 'DOCUMENT_DELETED') {
        this.unconfirmed = null;
        await this.markDeleted();
        return;
      }
      if (e.code === 'IDEMPOTENCY_MISMATCH') {
        // No debería ocurrir: se regenera el identificador para el contenido actual.
        this.unconfirmed = null;
        if (this.draft) {
          this.draft = { ...this.draft, operationId: newOperationId() };
          await this.deps.drafts.put(this.draft);
        }
        this.set({ state: { kind: 'error', message: e.message, retryable: true } });
        this.scheduleRetry();
        return;
      }
      if (e.retryable) {
        this.set({ state: { kind: 'error', message: e.message, retryable: true } });
        this.scheduleRetry();
        return;
      }
      // Error definitivo (validación, permisos): el pendiente se conserva para no perder texto.
      this.unconfirmed = null;
      this.set({ state: { kind: 'error', message: e.message, retryable: false } });
      return;
    }
    this.set({ state: { kind: 'error', message: (e as Error)?.message ?? 'Error', retryable: true } });
    this.scheduleRetry();
  }

  /** Conoce la cabecera real del servidor sin tocar el texto local (para comparar y combinar). */
  private async refreshHead(): Promise<void> {
    try {
      const res = await this.deps.client.call('document.get', { document_id: this.documentId });
      const h = res.head_revision;
      this.set({
        head: { id: h?.id ?? null, number: h?.number ?? null, content: (h?.content as DocumentContent | undefined) ?? null, createdAt: h?.created_at ?? null },
        openConflictIds: res.open_conflicts.map((c) => c.id),
      });
    } catch {
      // Sin red: se refrescará al reconectar o recargar.
    }
  }

  private async markDeleted(): Promise<void> {
    if (this.draft) {
      this.draft = { ...this.draft, state: 'deleted_remote' };
      await this.deps.drafts.put(this.draft);
    }
    this.set({ deletedRemotely: true, state: { kind: 'error', message: 'El documento fue eliminado en otro equipo', retryable: false } });
  }

  private scheduleRetry(): void {
    if (this.disposed || this.retryTimer) return;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.saveNow();
    }, this.deps.retryMs ?? 15_000);
  }

  /** Resuelve el conflicto abierto y recarga la revisión confirmada. */
  async resolve(conflictId: string, action: 'keep_current' | 'adopt_proposal' | 'merge', merged?: DocumentContent): Promise<void> {
    await this.deps.client.call('document.resolve', {
      conflict_id: conflictId,
      action,
      expected_head_revision_id: this.snapshot.head.id,
      ...(merged ? { merged_content: merged } : {}),
    });
    if (this.draft?.state === 'conflict') {
      await this.deps.drafts.remove(this.documentId, this.draft.operationId);
      this.draft = null;
    }
    await this.load();
  }

  /** Tras un borrado remoto, conserva el texto local como documento nuevo. */
  async recoverAsNew(): Promise<string> {
    const content = this.snapshot.localContent;
    if (!content) throw new Error('No hay texto local que recuperar');
    const res = await this.deps.client.call('document.recover_as_new', { source_document_id: this.documentId, content });
    if (this.draft) await this.deps.drafts.remove(this.documentId, this.draft.operationId);
    this.draft = null;
    return res.document_id;
  }
}
